import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import OrderDesign from './OrderDesign';
import OrderSidebarOrderList from './components/OrderSidebarOrderList';
import './components/OrderSidebarStyleCard.css';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import CustomDropdown from './CustomDropdown';
import './StrategyModal.css';
import { getStrategyHudTotal } from './utils/strategyHudTotal';

// Helper function to get rarity sort order
const getRarityOrder = (rarity) => {
  const rarityOrder = { 'common': 1, 'rare': 2, 'epic': 3, 'legendary': 4 };
  return rarityOrder[(rarity || 'common').toLowerCase()] || 0;
};

const MAX_HAND_SIZE = 10;

function getOrderIdNum(order) {
  const idNum = Number(order?.id ?? order?.order_id);
  return Number.isFinite(idNum) ? idNum : null;
}

/** Count physical copies per order id in a pile (hand, deck, battlefield, etc.). */
function countOrdersById(orders) {
  const counts = new Map();
  for (const order of orders || []) {
    const idNum = getOrderIdNum(order);
    if (idNum == null) continue;
    counts.set(idNum, (counts.get(idNum) || 0) + 1);
  }
  return counts;
}

/** True when the player has enough intel to play this order (matches hand afford checks in BattleUI). */
function canAffordOrderWithCurrentIntel(order, playerState) {
  if (!playerState || !order) return false;
  const intelNum = Number(playerState.intelligence);
  const costNum = Number(order.cost ?? 0);
  if (!Number.isFinite(intelNum) || !Number.isFinite(costNum)) return false;
  return intelNum >= costNum;
}

function StrategyModal({
  show,
  onClose,
  strategy,
  gameState,
  userId = null,
  hoveredOrderIndex,
  onHoverOrder,
  playerState,
  commandDirectiveActive = false,
  onCommandDirectiveSelect = null,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [hoveredOrderGroupKey, setHoveredOrderGroupKey] = useState(null);
  const [sortBy, setSortBy] = useState('cost'); // 'cost', 'rarity', 'name', 'type'

  if (!show || !strategy?.orders) return null;

  const handSize = Array.isArray(playerState?.hand) ? playerState.hand.length : 0;
  const isHandFull = handSize >= MAX_HAND_SIZE;

  // Expand orders based on count and track which ones are drawn
  const expandedOrders = [];
  const drawnCounts = new Map(); // Copies outside the deck (hand, board, discard, completed)

  if (playerState) {
    const allDrawnOrders = [
      ...(playerState.hand || []),
      ...(playerState.discard || []),
      ...(playerState.playedOrders?.front || []),
      ...(playerState.playedOrders?.flank || []),
      ...(playerState.completedOrders || []),
    ];
    for (const [idNum, n] of countOrdersById(allDrawnOrders)) {
      drawnCounts.set(idNum, n);
    }
  }

  const deckCounts = countOrdersById(playerState?.deck);

  strategy.orders.forEach((order) => {
    const idNum = getOrderIdNum(order);
    if (idNum == null) return;

    const strategyCount = order.count || 1;
    const drawnCount = drawnCounts.get(idNum) || 0;
    let deckSlotsRemaining = deckCounts.get(idNum) || 0;
    const physicalCount = drawnCount + deckSlotsRemaining;
    const displayCount = Math.min(strategyCount, physicalCount);

    for (let i = 0; i < displayCount; i++) {
      const isDrawn = i < drawnCount;
      const canCommandDirectiveGet =
        commandDirectiveActive && !isDrawn && deckSlotsRemaining > 0;
      if (canCommandDirectiveGet) {
        deckSlotsRemaining -= 1;
      }
      expandedOrders.push({
        ...order,
        isDrawn,
        canCommandDirectiveGet,
      });
    }
  });

  // Visual-only: include dynamically added units (e.g. Ter'fa-added Shadow Snipers)
  // that are present in current player piles but not in the saved base strategy list.
  const baseStrategyOrderIds = new Set((strategy.orders || []).map((o) => Number(o?.id)));
  const deckLikeOrders = [
    ...(playerState?.deck || []),
    ...(playerState?.drawPile || []),
    ...(playerState?.draw_pile || []),
  ];
  const allVisibleOrders = [...deckLikeOrders, ...(playerState?.hand || []), ...(playerState?.discard || []), ...(playerState?.playedOrders?.front || []), ...(playerState?.playedOrders?.flank || []), ...(playerState?.completedOrders || [])];
  const extraOrderTotalCounts = new Map();
  const extraOrderDrawnCounts = new Map();
  const extraOrderRepresentatives = new Map();

  const markExtraOrder = (order, isDrawn) => {
    const idNum = Number(order?.id);
    if (!Number.isFinite(idNum) || baseStrategyOrderIds.has(idNum)) return;
    extraOrderTotalCounts.set(idNum, (extraOrderTotalCounts.get(idNum) || 0) + 1);
    if (isDrawn) {
      extraOrderDrawnCounts.set(idNum, (extraOrderDrawnCounts.get(idNum) || 0) + 1);
    }
    if (!extraOrderRepresentatives.has(idNum)) {
      extraOrderRepresentatives.set(idNum, order);
    }
  };

  deckLikeOrders.forEach((order) => markExtraOrder(order, false));
  allVisibleOrders.forEach((order) => markExtraOrder(order, true));

  for (const [orderId, totalCount] of extraOrderTotalCounts.entries()) {
    const drawnCount = extraOrderDrawnCounts.get(orderId) || 0;
    const representative = extraOrderRepresentatives.get(orderId);
    if (!representative) continue;
    for (let i = 0; i < totalCount; i++) {
      expandedOrders.push({
        ...representative,
        isDrawn: i < drawnCount,
        isDynamicAdded: true,
      });
    }
  }
  
  // Sort by selected option
  expandedOrders.sort((a, b) => {
    // Primary sort by selected option
    let primaryComparison = 0;
    switch (sortBy) {
      case 'cost':
        primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
        break;
      case 'rarity':
        primaryComparison = getRarityOrder(a.rarity) - getRarityOrder(b.rarity);
        break;
      case 'name':
        primaryComparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'type':
        primaryComparison = (a.type || '').localeCompare(b.type || '');
        break;
      default:
        primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
    }

    if (primaryComparison !== 0) return primaryComparison;

    // Secondary sort by name (except when already sorting by name)
    if (sortBy !== 'name') {
      return (a.name || '').localeCompare(b.name || '');
    }

    // If sorting by name and names are equal, sort by cost
    return (a.cost ?? 0) - (b.cost ?? 0);
  });
  
  const hoveredOrder = hoveredOrderIndex !== null ? expandedOrders[hoveredOrderIndex] : null;

  const modalTree = (
    <div
      className={`strategy-modal-overlay${isMobile ? ' strategy-modal-overlay--mobile' : ''}`}
      data-no-battle-flashlight="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Desktop: hover preview beside modal — off-screen on mobile */}
      {hoveredOrder && !isMobile && (
        <div
          style={{
            position: 'absolute',
            left: 'calc(50% + 300px)',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '20px',
            zIndex: 15101,
            pointerEvents: 'none',
          }}
        >
          <OrderDesign
            order={hoveredOrder}
            count={0}
            minimal={false}
            lazyLoad={true}
            gameState={gameState}
            userId={userId}
          />
        </div>
      )}
      <div
        className={`strategy-modal-panel${isMobile ? ' strategy-modal-panel--mobile' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="strategy-modal-header">
          <h3 style={{ color: 'white', margin: 0 }}>
            {t('battleUI.strategy') || 'Strategy'}
          </h3>
          <button
            type="button"
            className="strategy-modal-close-btn"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              onClose();
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#444';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#333';
            }}
          >
            {t('battleUI.close') || 'Close'}
          </button>
        </div>

        <div className="strategy-modal-sort-row">
          <div style={{ color: '#aaa', fontSize: isMobile ? '0.8em' : '0.9em' }}>
            {t('battleUI.totalStrategyOrders') || 'Total'}: {getStrategyHudTotal(playerState)}
          </div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              color: '#aaa',
              fontSize: isMobile ? '0.8em' : '0.9em',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
            }}
          >
            {t('strategy.sortBy') || 'Sort by:'}
            <div
              className="strategy-modal-sort-dropdown"
              style={{ minWidth: isMobile ? undefined : '150px', maxWidth: isMobile ? '100%' : '150px' }}
            >
              <CustomDropdown
                options={[
                  { value: 'cost', label: t('strategy.sort.cost') || 'Cost' },
                  { value: 'rarity', label: t('strategy.sort.rarity') || 'Rarity' },
                  { value: 'name', label: t('strategy.sort.name') || 'Name' },
                  { value: 'type', label: t('strategy.sort.type') || 'Type' },
                ]}
                value={sortBy}
                onChange={(value) => {
                  playSoundEffect('/sounds/click.mp3');
                  setSortBy(value);
                }}
                placeholder={t('strategy.sortBy') || 'Sort by:'}
              />
            </div>
          </label>
        </div>

        <div className="strategy-modal-list">
          <OrderSidebarOrderList
            items={expandedOrders}
            getGroupKey={(order) => `${order.id}|${order.isDrawn ? 'drawn' : 'active'}`}
            getDisplayName={(order) => order.name || 'Unknown Order'}
            getDisplayCost={(order) => order.cost ?? 'N/A'}
            costLabel={t('strategy.cost')}
            renderRowWrapperClassName={(order, group) => {
              const isDrawn = order.isDrawn || false;
              const groupKey = group.key;
              const isHovered = hoveredOrderGroupKey === groupKey;
              const showGetButton =
                commandDirectiveActive &&
                !isDrawn &&
                order.canCommandDirectiveGet &&
                !order.isDynamicAdded &&
                onCommandDirectiveSelect &&
                (isMobile || isHovered);
              return `${isDrawn ? 'strategy-modal-order-entry--drawn' : ''}${
                showGetButton ? ' strategy-modal-order-entry--with-actions' : ''
              }`.trim();
            }}
            onItemMouseEnter={(order, group) => {
              if (!isMobile) {
                playSoundEffect('/sounds/hoverover.mp3', 0.3);
              }
              onHoverOrder(group.firstIndex);
              setHoveredOrderGroupKey(group.key);
            }}
            onItemMouseLeave={() => {
              onHoverOrder(null);
              setHoveredOrderGroupKey(null);
            }}
            renderAfterCard={(order, group) => {
              const isDrawn = order.isDrawn || false;
              const isHovered = hoveredOrderGroupKey === group.key;
              const canAffordGet = canAffordOrderWithCurrentIntel(order, playerState);
              const showGetButton =
                commandDirectiveActive &&
                !isDrawn &&
                order.canCommandDirectiveGet &&
                !order.isDynamicAdded &&
                onCommandDirectiveSelect &&
                (isMobile || isHovered);
              if (!showGetButton) return null;
              return (
                <button
                  type="button"
                  className={`strategy-modal-get-btn${canAffordGet && !isHandFull ? ' strategy-modal-get-btn--affordable' : ''}`}
                  disabled={isHandFull}
                  title={isHandFull ? (t('battleUI.handFullCannotGet') || 'Hand is full (10/10)') : undefined}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isHandFull) return;
                    onCommandDirectiveSelect(order.id);
                  }}
                >
                  {t('battleUI.get') || 'Get'}
                </button>
              );
            }}
          />
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return modalTree;
  }
  return createPortal(modalTree, document.body);
}

export default StrategyModal;

